import { createEvent, restore, sample } from 'effector'
import { fx } from '../../../../libs'
import { UserEdit, UserEditRequest } from './types'
import { $params } from '../../../../features/router'

export const userEditProfileMounted = createEvent()
export const setUserEditProfile = createEvent<UserEdit>()
export const setUserBan = createEvent()
export const setUserDelete = createEvent()

export const submitEditUser = createEvent<UserEditRequest>()

export const closeModal = createEvent()

export const attachGroupMounted = createEvent<{ groupId: string }>()
export const detachGroupMounted = createEvent<{ groupId: string }>()

export const attachExtraPermissionMounted = createEvent<{
  permissionId: string
}>()
export const detachExtraPermissionMounted = createEvent<{
  permissionId: string
}>()

export const attachRevokedPermissionMounted = createEvent<{
  permissionId: string
}>()
export const detachRevokedPermissionMounted = createEvent<{
  permissionId: string
}>()

export const permissionsOptionsMounted = createEvent()

export const $userEditProfile = restore(setUserEditProfile, {
  id: 0,
  username: '',
  firstName: '',
  middleName: '',
  lastName: '',
  email: '',
  password: '',
  position: '',
  verified: false,
  country: '',
  fullName: '',
  hasDeposit: false,
  partner: false,
  registeredAt: '',
  address: '',
  phone: '',
  dateOfBirth: '',
  socialNetwork: '',
  avatarUrl: '',
  gender: '',
  userGroups: [],
  extraPermissions: [],
  revokedPermissions: [],
})

export const getUser = fx<void, UserEdit>({
  method: 'GET',
  url: '/user/',
})

export const attachGroup = fx<void, void>({
  method: 'POST',
  url: '/user/{userId}/group/{groupId}',
})

export const detachGroup = fx<void, void>({
  method: 'DELETE',
  url: '/user/{userId}/group/{groupId}',
})

export const attachExtraPermission = fx<{ permissionId: string }, void>({
  method: 'POST',
  url: '/user/{userId}/attach_extra_permission',
})

export const detachExtraPermission = fx<{ permissionId: string }, void>({
  method: 'POST',
  url: '/user/{userId}/detach_extra_permission',
})

export const attachRevokedPermission = fx<{ permissionId: string }, void>({
  method: 'POST',
  url: '/user/{userId}/attach_revoked_permission',
})

export const detachRevokedPermission = fx<{ permissionId: string }, void>({
  method: 'POST',
  url: '/user/{userId}/detach_revoked_permission',
})

export const getAllPermissionsForOptions = fx<
  void,
  { items: { id: string; name: string; description: string }[]; total: number }
>({
  method: 'GET',
  url: '/permission',
})

export const $allPermissionsOptions = restore(
  getAllPermissionsForOptions.doneData.map((a) => a.body.items || []),
  [] as { id: string; name: string; description: string }[],
)

export const banUser = fx<UserEdit, void>({
  method: 'PUT',
  url: '/user/',
})

export const updateUser = fx<UserEditRequest, UserEdit>({
  method: 'PATCH',
  url: '/user/',
})

export const deleteUser = fx<UserEdit, void>({
  method: 'DELETE',
  url: '/user/',
})

sample({
  clock: userEditProfileMounted,
  source: $params,
  fn: (params) => {
    let id
    if (params) {
      id = params.userID
    }
    return {
      url: `/user/${id}`,
      query: {
        populate: JSON.stringify([
          'User.userGroups',
          'UserGroup.name',
          'Role.permissions',
          'Permission',
        ]),
      },
    }
  },
  target: getUser,
})

sample({
  clock: setUserBan,
  source: $params,
  fn: (params) => {
    let id
    if (params) {
      id = params.userID
    }
    return {
      url: `/user/${id}`,
    }
  },
  target: banUser,
})

sample({
  clock: submitEditUser,
  source: $params,
  fn: (params, user) => {
    let id
    if (params) {
      id = params.userID
    }
    return {
      url: `/user/${id}`,
      body: { ...user },
    }
  },
  target: updateUser,
})

sample({
  clock: setUserDelete,
  source: $params,
  fn: (params) => {
    let id
    if (params) {
      id = params.userID
    }
    return {
      url: `/user/${id}`,
    }
  },
  target: deleteUser,
})

// update profile model

sample({
  clock: getUser.doneData,
  fn: (doneData) => doneData.body,
  target: setUserEditProfile,
})
sample({
  clock: updateUser.doneData,
  fn: (doneData) => doneData.body,
  target: setUserEditProfile,
})

export const $isSuccessCreate = restore(
  updateUser.doneData.map(() => true),
  false,
).reset(closeModal)

sample({
  clock: attachGroupMounted,
  source: $params,
  fn: (params, { groupId }) => ({
    url: `/user/${params?.userID ?? ''}/group/${groupId}`,
  }),
  target: attachGroup,
})

sample({
  clock: detachGroupMounted,
  source: $params,
  fn: (params, { groupId }) => ({
    url: `/user/${params?.userID ?? ''}/group/${groupId}`,
  }),
  target: detachGroup,
})

sample({
  clock: attachExtraPermissionMounted,
  source: $params,
  fn: (params, { permissionId }) => ({
    url: `/user/${params?.userID ?? ''}/attach_extra_permission`,
    body: { permissionId: permissionId },
  }),
  target: attachExtraPermission,
})

sample({
  clock: detachExtraPermissionMounted,
  source: $params,
  fn: (params, { permissionId }) => ({
    url: `/user/${params?.userID ?? ''}/detach_extra_permission`,
    body: { permissionId: permissionId },
  }),
  target: detachExtraPermission,
})

sample({
  clock: attachRevokedPermissionMounted,
  source: $params,
  fn: (params, { permissionId }) => ({
    url: `/user/${params?.userID ?? ''}/attach_revoked_permission`,
    body: { permissionId: permissionId },
  }),
  target: attachRevokedPermission,
})

sample({
  clock: detachRevokedPermissionMounted,
  source: $params,
  fn: (params, { permissionId }) => ({
    url: `/user/${params?.userID ?? ''}/detach_revoked_permission`,
    body: { permissionId: permissionId },
  }),
  target: detachRevokedPermission,
})

sample({
  clock: [
    attachGroup.done,
    detachGroup.done,
    attachExtraPermission.done,
    detachExtraPermission.done,
    attachRevokedPermission.done,
    detachRevokedPermission.done,
  ],
  target: userEditProfileMounted,
})

sample({
  clock: permissionsOptionsMounted,
  fn: () => ({ query: { limit: '1000', page: '1' } }),
  target: getAllPermissionsForOptions,
})
